import AppConstants from "../../constants/AppConstants";

const DESKTOP_GUTTER_WIDTH = 24;
const MOBILE_GUTTER_WIDTH = 12;
const DESKTOP_COLUMN_COUNT = 12;
const MOBILE_COLUMN_COUNT = 3;

/**
 * @typedef Immutable
 * @namespace Immutable
 */

/**
 * Immutable Map is an unordered <code>Collection.Keyed</code> of <code>(key, value)</code> pairs
 * with <code>O(log32 N)</code> gets and <code>O(log32 N)</code> persistent sets.
 * @typedef Immutable.Map
 * @memberof Immutable
 */

/**
 * Snap and guide information for a single point.
 * @typedef {[snapPoint: number, lowerGuideBound: number, upperGuideBound: number, strokeType: string]} SnapPoint
 * @property {number} axisOffset - the x or y coordinate to snap to (depending on the axis) - opposite axis starting point. Ex: if the snap point is along the x axis, this would be the y positioning of that axis.
 * @property {number} lowerGuideBound - the lower bound of the snap guide to show when this snap is selected
 * @property {number} upperGuideBound - the upper bound of the snap guide to show when this snap is selected
 * @property {string} strokeType - the stroke type to use when drawing the snap guide (defaults to "dashed" if not specified)
 */

/**
 * Creates a sorted collection of x and y snap points with guide data for a given canvas
 * @param {{
 *  blockHeight: number,
 *  layout: string,
 *  selectedBlock: Immutable.Map,
 *  viewState: Immutable.Map,
 * }} props - The props to use to create the snap points
 * @returns {{
 *  x: [SnapPoint],
 *  y: [SnapPoint],
 * }} - A sorted collection of x and y snap points with guide data for a given canvas
 */

export const GetSelectedCanvasSnapPoints = ({ blockHeight, layout, selectedBlock, viewState }) => {
  const stageWidth =
    layout === AppConstants.LAYOUT_DESKTOP
      ? AppConstants.LAYOUT_DESKTOP_WIDTH
      : AppConstants.LAYOUT_MOBILE_WIDTH;
  const showColumnOverlay = viewState.getIn(["stage", "overlays", "columnGuides"]);
  const enableSnapping = viewState.getIn(["stage", "overlays", "enableSnapping"]);

  const columnGuides =
    showColumnOverlay && enableSnapping
      ? getColumnGuidePoints(blockHeight, stageWidth, layout)
      : [];
  const centerGuidePoints = enableSnapping
    ? getCenterGuidePoints(blockHeight, stageWidth)
    : { x: [], y: [] };
  const unselectedElementPoints = enableSnapping
    ? getUnselectedElementPoints(selectedBlock, layout)
    : { x: [], y: [] };
  const selectedCanvasBoundaryPoints = enableSnapping
    ? getCanvasBoundaryGuides(blockHeight, stageWidth)
    : { x: [], y: [] };
  return {
    x: sortAndMergeDuplicatePoints([
      ...columnGuides,
      ...unselectedElementPoints.x,
      ...centerGuidePoints.x,
      ...selectedCanvasBoundaryPoints.x,
    ]),
    y: sortAndMergeDuplicatePoints([
      ...unselectedElementPoints.y,
      ...centerGuidePoints.y,
      ...selectedCanvasBoundaryPoints.y,
    ]),
  };
};

/**
 * Creates a collection of x and y snap points with guide data for columns of a given canvas and layout
 * @param {number} blockHeight - the block height of the selected canvas
 * @param {number} stageWidth - the stage width to use when creating the snap points.
 * @param {string} layout - the layout to calculate column snaps for.
 * @returns {{
 *  x: [SnapPoint],
 *  y: [SnapPoint],
 * }} - A collection of x and y snap points with guide data for columns of a given canvas and layout
 */

export const getColumnGuidePoints = (blockHeight, stageWidth, layout) => {
  const isDesktop = layout === AppConstants.LAYOUT_DESKTOP;

  const gutterWidth = isDesktop ? DESKTOP_GUTTER_WIDTH : MOBILE_GUTTER_WIDTH;
  const columnCount = isDesktop ? DESKTOP_COLUMN_COUNT : MOBILE_COLUMN_COUNT;

  const columnWidth = (stageWidth - gutterWidth * (columnCount + 1)) / columnCount;
  const columnPoints = [];
  Array(columnCount + 1)
    .fill(0)
    .forEach((_, i) => {
      columnPoints.push([(columnWidth + gutterWidth) * i, 0, blockHeight]);
      columnPoints.push([(columnWidth + gutterWidth) * i + gutterWidth, 0, blockHeight]);
    });
  return columnPoints;
};

/**
 * Creates a collection of x and y snap points with guide data for the centers of a given canvas and layout
 * @param {number} blockHeight - the block height of the selected canvas
 * @param {number} stageWidth - the stage width to use when creating the snap points.
 * @returns {{
 *  x: [SnapPoint],
 *  y: [SnapPoint],
 * }} - A collection of x and y snap points with guide data for the centers of a given canvas and layout
 */

export const getCenterGuidePoints = (blockHeight, stageWidth) => ({
  x: [[stageWidth / 2, 0, blockHeight, "solid"]],
  y: [[blockHeight / 2, 0, stageWidth, "solid"]],
});

/**
 * Creates a collection of parsed precalculated x and y snap points with guide data for unselected elements in the selected canvas
 * pulled from the elementSelect data-snap-points attribute
 * @param {Immutable.Map} selectedBlock - the selected canvas used to get id for querying unselected elements
 * @param {string} layout - the layout to calculate column snaps for.
 * @returns {{
 *  x: [SnapPoint],
 *  y: [SnapPoint],
 * }} - A collection of parsed precalculated x and y snap points with guide data for unselected elements in the selected canvas
 */

export const getUnselectedElementPoints = (selectedBlock, layout) => {
  const unSelectedVisibleElements = document.querySelectorAll(
    `#si_block_${layout}_${selectedBlock.get(
      AppConstants.DESIGN_ITEM_PROP_ID
    )} .selectable:not(.selected)`
  );

  let snapPoints = { x: [], y: [] };
  for (var i = 0; i < unSelectedVisibleElements.length; i++) {
    const elementSnaps = JSON.parse(unSelectedVisibleElements[i].dataset.snapPoints || "{}");
    snapPoints.x = snapPoints.x.concat(elementSnaps?.x || []);
    snapPoints.y = snapPoints.y.concat(elementSnaps?.y || []);
  }
  return snapPoints;
};

/**
 * Creates a collection of x and y snap points with guide data for the canvas boundary of a given canvas and layout
 * @param {number} blockHeight - the block height of the selected canvas
 * @param {number} stageWidth - the stage width to use when creating the snap points.
 * @returns {{
 *  x: [SnapPoint],
 *  y: [SnapPoint],
 * }} - A collection of x and y snap points with guide data for the canvas boundary of a given canvas and layout
 */

export const getCanvasBoundaryGuides = (blockHeight, stageWidth) => ({
  x: [
    [stageWidth, 0, blockHeight, "solid"],
    [0, 0, blockHeight, "solid"],
  ],
  y: [
    [blockHeight, 0, stageWidth, "solid"],
    [0, 0, stageWidth, "solid"],
  ],
});

/**
 * Creates a sorted and unique collection of x and y snap points.
 * If a duplicate snap point is found the guide min and max values are expanded accordingly upon merge.
 * @param {[SnapPoint]} points - the block height of the selected canvas
 * @returns {{
 *  x: [SnapPoint],
 *  y: [SnapPoint],
 * }} - A sorted and unique collection of x and y snap points.
 */

export const sortAndMergeDuplicatePoints = (points) => {
  const len = points.length;
  if (len <= 1) return points;
  const sortedPoints = points.sort((a, b) => (a[0] > b[0] ? 1 : -1));
  const uniqPoints = [sortedPoints[0]];
  for (let i = 1; i < len; i++) {
    if (sortedPoints[i - 1][0] !== sortedPoints[i][0]) {
      uniqPoints.push(sortedPoints[i]);
    } else {
      sortedPoints[i - 1][1] = Math.min(sortedPoints[i][1], sortedPoints[i - 1][1]);
      sortedPoints[i - 1][2] = Math.max(sortedPoints[i][2], sortedPoints[i - 1][2]);
      !sortedPoints[i - 1][3] &&
        sortedPoints[i][3] &&
        (sortedPoints[i - 1][3] = sortedPoints[i][3]);
    }
  }
  return uniqPoints;
};
